/**
 * 74.12 step 1 (DECISIONS 365, 367, 368; ARCHITECTURE §5.46): the climate per vertex, on the world sheet — what the biomes
 * (74.3 step 1) and the weather (74.11) will read, one source for both, so a wet valley grows the wood and gets the fog.
 *
 * Per vertex: the **latitude** (the sheet's, about 74.3's spin axis — the sun's axis since C's wind-axis step, so the bands,
 * the lee and the day agree), the **moisture** (the sheet's wet mask: drainage, basins, the lee against the wind, the
 * archetype's wetness), the **temperature** (the archetype's base, colder toward the poles, with height, and in thin air —
 * 74.10's `airOf` density), and a **climate cell**: wet or dry × cold, temperate or hot × low or high (twelve). The sheet's
 * own cell (wet/dry × low/high) stays as it is; this one carries the warmth.
 *
 * Pure (dmath), derived once from the generated mesh (the sheet's rule: a crater moves nothing), cached per sheet. Nothing in
 * the sim reads it; the planet table draws it behind the Lab's *Climate sheet* row.
 */
import type { PlanetState, PlanetArchetype } from '../types';
import { worldSheetOf, type WorldSheet } from './worldSheet';
import { airOf } from './air';
import { dcos } from './dmath';
import { nearestSurfaceVertex } from './surface';
import type { WeatherKind } from './weather';

export const CLIMATE = {
  /** how much of the scale the poles lose against the equator (1 − cos latitude), the summits against the lowland (the elevation band), and an airless world against a full one (1 − the air's density) */
  poleCooling: 0.4, heightCooling: 0.3, thinAirCooling: 0.25,
  /** the warmth's edges on the 0–1 scale: cold under `coldAt`, hot from `hotAt` */
  coldAt: 0.33, hotAt: 0.66,
};

/** The archetype's base temperature at its equator's lowland with full air (0 frozen … 1 scorching): bruma's cold, sonora's and ochrus's heat, the temperate worlds between. */
export const ARCHETYPE_TEMPERATURE: Record<PlanetArchetype, number> = { thalia: 0.6, viridis: 0.72, pelago: 0.64, bruma: 0.22, pallor: 0.52, ochrus: 0.84, tephra: 0.76, murex: 0.5, sonora: 0.92 };

export type Warmth = 'cold' | 'temperate' | 'hot';
export type Wetness = 'wet' | 'dry';
export type Height = 'low' | 'high';
export type ClimateCellId = `${Wetness}${'Cold' | 'Temperate' | 'Hot'}${'Low' | 'High'}`;
export const CLIMATE_CELLS: readonly ClimateCellId[] = (['wet', 'dry'] as const).flatMap(w => (['Cold', 'Temperate', 'Hot'] as const).flatMap(t => (['Low', 'High'] as const).map(h => `${w}${t}${h}` as ClimateCellId)));

export interface Climate {
  /** 0–1 per vertex */
  temperature: Float32Array;
  warmth: Warmth[];
  cell: ClimateCellId[];
  /** how many vertices each cell holds */
  counts: Record<ClimateCellId, number>;
  /** the world's: the air's density read, the mean temperature */
  airDensity: number; meanTemperature: number;
}

const cache = new WeakMap<WorldSheet, Climate>();

/** The warmth band of a temperature. */
export function warmthOf(t: number): Warmth { return t < CLIMATE.coldAt ? 'cold' : t < CLIMATE.hotAt ? 'temperate' : 'hot'; }

/** The temperature at a vertex from its parts (0–1): the base, cooled toward the poles, with height and in thin air. */
export function temperatureAt(base: number, latitude: number, elevation: number, airDensity: number): number {
  const t = base - CLIMATE.poleCooling * (1 - dcos(latitude)) - CLIMATE.heightCooling * elevation - CLIMATE.thinAirCooling * (1 - Math.max(0, Math.min(1, airDensity)));
  return Math.max(0, Math.min(1, t));
}

/** The climate of a planet on its world sheet (built once per sheet). */
export function climateOf(planet: PlanetState, sheet: WorldSheet = worldSheetOf(planet)): Climate {
  const hit = cache.get(sheet);
  if (hit) return hit;
  const V = sheet.graph.count;
  const base = ARCHETYPE_TEMPERATURE[planet.archetype as PlanetArchetype] ?? 0.55;
  const airDensity = airOf(planet).density;
  const temperature = new Float32Array(V), warmth: Warmth[] = new Array(V), cell: ClimateCellId[] = new Array(V);
  const counts = Object.fromEntries(CLIMATE_CELLS.map(c => [c, 0])) as Record<ClimateCellId, number>;
  let sum = 0;
  for (let v = 0; v < V; v++) {
    const t = temperatureAt(base, sheet.latitude[v], sheet.elevation[v], airDensity);
    temperature[v] = t; sum += t;
    const w = warmthOf(t);
    warmth[v] = w;
    const id = `${sheet.cell[v].startsWith('wet') ? 'wet' : 'dry'}${w === 'cold' ? 'Cold' : w === 'hot' ? 'Hot' : 'Temperate'}${sheet.cell[v].endsWith('High') ? 'High' : 'Low'}` as ClimateCellId;
    cell[v] = id; counts[id]++;
  }
  const out: Climate = { temperature, warmth, cell, counts, airDensity, meanTemperature: V ? sum / V : 0 };
  cache.set(sheet, out);
  return out;
}

/** The table's colour of a cell (the Lab's Climate sheet row): cold blue-white, temperate green, hot orange; wet saturated, dry pale; high darker. */
export function climateCellColor(id: ClimateCellId): string {
  const hot = id.includes('Hot'), cold = id.includes('Cold'), wet = id.startsWith('wet'), high = id.endsWith('High');
  const [r, g, b] = cold ? [170, 200, 240] : hot ? [240, 160, 70] : [110, 190, 110];
  const sat = wet ? 1 : 0.55, dark = high ? 0.72 : 1;
  const mix = (c: number) => Math.round((c * sat + 200 * (1 - sat)) * dark);
  return `rgba(${mix(r)},${mix(g)},${mix(b)},0.38)`;
}

// ---- 74.11 (a) (DECISIONS 373): what a front carries is the climate's, the hour's and the air's -----------------------------------

/**
 * The weather a front carries, by the climate cell under its birth place (§5.48: twelve mixes in place of the archetype's
 * one): a wet cold cell snows and fogs, a wet hot one rains and storms, a dry hot one dusts, a dry cold one is clear and
 * hazy. The shares are a draw's weights (core/weather.ts `draw`), as `BIOME_WEATHER`'s are.
 */
export const CLIMATE_WEATHER: Record<ClimateCellId, Partial<Record<WeatherKind, number>>> = {
  wetColdLow: { clear: 0.3, haze: 0.2, snow: 0.3, fog: 0.2 },
  wetColdHigh: { clear: 0.25, haze: 0.2, snow: 0.4, fog: 0.1, storm: 0.05 },
  wetTemperateLow: { clear: 0.4, haze: 0.2, rain: 0.25, fog: 0.15 },
  wetTemperateHigh: { clear: 0.35, haze: 0.2, rain: 0.2, snow: 0.1, fog: 0.15 },
  wetHotLow: { clear: 0.4, haze: 0.2, rain: 0.25, storm: 0.15 },
  wetHotHigh: { clear: 0.4, haze: 0.2, rain: 0.25, storm: 0.1, fog: 0.05 },
  dryColdLow: { clear: 0.55, haze: 0.3, snow: 0.1, dust: 0.05 },
  dryColdHigh: { clear: 0.5, haze: 0.3, snow: 0.15, fog: 0.05 },
  dryTemperateLow: { clear: 0.55, haze: 0.3, rain: 0.05, dust: 0.1 },
  dryTemperateHigh: { clear: 0.55, haze: 0.3, rain: 0.05, storm: 0.05, fog: 0.05 },
  dryHotLow: { clear: 0.5, haze: 0.25, dust: 0.25 },
  dryHotHigh: { clear: 0.55, haze: 0.3, dust: 0.1, storm: 0.05 },
};
/** What an archetype adds to every cell's mix: tephra's ash is the volcano's, not the climate's; murex's lantern fogs. */
export const ARCHETYPE_WEATHER_EXTRA: Partial<Record<PlanetArchetype, Partial<Record<WeatherKind, number>>>> = { tephra: { ash: 0.3 }, murex: { fog: 0.1 } };
/** The hour's weights on a front's draw (74.2's clock, a fraction of the day — 0.25 sunrise, 0.5 noon, 0.75 sunset): fog at dawn, storms in the afternoon, clear nights. */
export const HOUR_WEATHER = { dawn: [0.2, 0.36] as const, dawnFog: 2, afternoon: [0.55, 0.72] as const, afternoonStorm: 1.5, night: [0.85, 0.12] as const, nightClear: 1.2 };
/** The air's gate: under `thin` density no rain, snow, fog or storm (dust and haze only); an airless world is clear always. */
export const AIR_WEATHER = { thin: 0.5 };
const WET: readonly WeatherKind[] = ['rain', 'snow', 'fog', 'storm'];

/** The hour's multipliers (1 where the hour says nothing). */
export function hourWeights(hour: number): Partial<Record<WeatherKind, number>> {
  const h = hour - Math.floor(hour);
  const inBand = (b: readonly [number, number]) => b[0] <= b[1] ? h >= b[0] && h < b[1] : h >= b[0] || h < b[1];
  const w: Partial<Record<WeatherKind, number>> = {};
  if (inBand(HOUR_WEATHER.dawn)) w.fog = HOUR_WEATHER.dawnFog;
  if (inBand(HOUR_WEATHER.afternoon)) w.storm = HOUR_WEATHER.afternoonStorm;
  if (inBand(HOUR_WEATHER.night)) w.clear = HOUR_WEATHER.nightClear;
  return w;
}

/** The climate cell under a place (the nearest generated vertex's). */
export function climateCellAt(planet: PlanetState, dir: { x: number; y: number; z: number }): ClimateCellId {
  const sheet = worldSheetOf(planet);
  return climateOf(planet, sheet).cell[sheet.graph.nodeOf[nearestSurfaceVertex(planet, dir)]];
}

/** A front's mix at its birth place and hour: the climate cell's, the archetype's extra, × the hour's weights, under the air's gate. Pure. */
export function frontMix(planet: PlanetState, birthPlace: { x: number; y: number; z: number }, hour: number, airDensity: number): Partial<Record<WeatherKind, number>> {
  if (!(airDensity > 0)) return { clear: 1 };
  const base = CLIMATE_WEATHER[climateCellAt(planet, birthPlace)], extra = ARCHETYPE_WEATHER_EXTRA[planet.archetype as PlanetArchetype] ?? {}, hw = hourWeights(hour);
  const mix: Partial<Record<WeatherKind, number>> = {};
  for (const k of Object.keys(base) as WeatherKind[]) mix[k] = (base[k] ?? 0) + (extra[k] ?? 0);
  for (const k of Object.keys(extra) as WeatherKind[]) if (mix[k] === undefined) mix[k] = extra[k];
  for (const k of Object.keys(mix) as WeatherKind[]) mix[k] = (mix[k] ?? 0) * (hw[k] ?? 1);
  if (airDensity < AIR_WEATHER.thin) for (const k of WET) delete mix[k];
  return mix;
}
